<template>
    <div class="h-screen">
        <div class="absolute inset-x-0 mt-48 md:mt-64 px-8 z-10">
            <select class="appearance-none block mx-auto px-4 py-2 md:px-6 md:py-4 w-full max-w-sm bg-white rounded-full text-center focus:outline-none" style="text-align-last: center;" v-model="what">
                <option disabled :value="null">What are you recycling?</option>
                <option :value="''">Show all</option>
                <option v-for="what in whats" :value="what">{{ what }}</option>
            </select>
        </div>

        <div class="block w-full h-full map" ref="map"></div>
    </div>
</template>

<script>
    import mapboxgl from 'mapbox-gl/dist/mapbox-gl';
    mapboxgl.accessToken = 'pk.eyJ1Ijoic2dyb3VwZGVzaWduIiwiYSI6ImNqemtzNmdnMTBtMWIzZG85bDhpMW5ieWwifQ.lzuSfP93ggNuMp8QTalT4w';

    export default {
        name: 'WasteMap',
        props: {
            locations: {
                type: Array,
                required: true,
            },
            whats: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                map: null,
                what: null,
            };
        },
        computed: {
            filteredLocations() {
                if (!this.what) {
                    return this.locations;
                } else {
                    return this.locations.filter(location => {
                        return location.what.indexOf(this.what) !== -1;
                    });
                }
            }
        },
        watch: {
            what() {
                this.draw();
            }
        },
        mounted() {
            this.map = new mapboxgl.Map({
                container: this.$refs.map,
                style: 'mapbox://styles/mapbox/streets-v11',
                center: [147.276013, -42.832893],
                zoom: 12
            });

            const size = 120;
            const _this = this;

            const pulsingDot = {
                width: size,
                height: size,
                data: new Uint8Array(size * size * 4),

                onAdd: function() {
                    const canvas = document.createElement('canvas');
                    canvas.width = this.width;
                    canvas.height = this.height;
                    this.context = canvas.getContext('2d');
                },

                render: function() {
                    const duration = 1600;
                    const t = (performance.now() % duration) / duration;

                    const radius = size / 2 * 0.3;
                    const outerRadius = size / 2 * 0.7 * t + radius;
                    const context = this.context;

                    // draw outer circle
                    context.clearRect(0, 0, this.width, this.height);
                    context.beginPath();
                    context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
                    context.fillStyle = 'rgba(0, 160, 146,' + (1 - t) + ')';
                    context.fill();

                    // draw inner circle
                    context.beginPath();
                    context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
                    context.fillStyle = 'rgb(0, 160, 146)';
                    context.strokeStyle = 'white';
                    context.lineWidth = 2 + 4 * (1 - t);
                    context.fill();
                    context.stroke();

                    // update this image's data with data from the canvas
                    this.data = context.getImageData(0, 0, this.width, this.height).data;

                    // keep the map repainting
                    _this.map.triggerRepaint();

                    // return `true` to let the map know that the image was updated
                    return true;
                }
            };

            this.map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 });

            this.map.on('load', () => {
                this.draw();

                this.map.on('click', 'points', event => {
                    const coordinates = event.features[0].geometry.coordinates.slice();
                    const location = event.features[0].properties;

                    while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
                        coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
                    }

                    const whats = JSON.parse(location.what);

                    let html = `<h2 class="h4 mt-0 mb-2">${location.title}</h2>`;
                    html += `<p class="mt-0 mb-4">${location.address}</p>`;
                    html += '<h3 class="h5 mt-0 mb-2">What can I recycle?</h3>';
                    html += '<ul class="mt-2 pl-4">';

                    for (const what of whats) {
                        html += `<li class="list-disc">${what}</li>`
                    }

                    html += '</ul>';

                    if (location.notes && location.notes !== 'null') {
                        html += `<div class="mt-4">${location.notes}</div>`;
                    }

                    new mapboxgl.Popup()
                        .setLngLat(coordinates)
                        .setHTML(html)
                        .addTo(this.map);
                });

                this.map.on('mouseenter', 'points', () => {
                    this.map.getCanvas().style.cursor = 'pointer';
                });

                this.map.on('mouseleave', 'points', () => {
                    this.map.getCanvas().style.cursor = '';
                });
            });
        },
        methods: {
            draw() {
                const bounds = new mapboxgl.LngLatBounds();

                for (const location of this.filteredLocations) {
                    bounds.extend(location.location);
                }

                // Zoom to fit all markers on the map with 100px of padding around the sides.
                this.map.fitBounds(bounds, {
                    padding: 100,
                    maxZoom: 13.5
                });

                if (this.map.getLayer('points')) {
                    this.map.removeLayer('points');
                    this.map.removeSource('points');
                }

                this.map.addLayer({
                    id: 'points',
                    type: 'symbol',
                    source: {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: this.filteredLocations.map(location => ({
                                type: 'Feature',
                                properties: location,
                                geometry: {
                                    type: 'Point',
                                    coordinates: location.location
                                }
                            }))
                        }
                    },
                    layout: {
                        'icon-image': 'pulsing-dot'
                    }
                });
            }
        }
    };
</script>
